import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import MinusBlack from 'svg/MinusBlack';
import PlusBlack from 'svg/PlusBlack';
import classNames from 'styles/utils/classNames';
import {
  ParticipantAndGamesArray,
  eventsSchema,
  participantsAndGamesSchema,
  whenAndWhereSchema,
} from '../types';

type WhenWhereFormFormSchema = z.infer<typeof whenAndWhereSchema>;
type EventFormSchema = z.infer<typeof eventsSchema>;
type ParticipantsAndGameFormSchema = z.infer<typeof participantsAndGamesSchema>;

type Props = {
  label: string;
  name: keyof WhenWhereFormFormSchema | keyof EventFormSchema | keyof ParticipantsAndGameFormSchema;
  register:
    | UseFormRegister<WhenWhereFormFormSchema>
    | UseFormRegister<any>
    | UseFormRegister<ParticipantsAndGameFormSchema>;
  errors:
    | FieldErrors<WhenWhereFormFormSchema>
    | FieldErrors<EventFormSchema>
    | FieldErrors<ParticipantsAndGameFormSchema>;
  minValue?: number;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  control: Control<any>;
  labelClassName?: string;
};

const Counter = ({
  label,
  name,
  register,
  errors,
  minValue = 1,
  inputProps,
  setValue,
  getValues,
  control,
  labelClassName = 'typography-product-subheading mb-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
}: Props) => {
  const inputNativeType = inputProps?.type;

  const [disableMinus, setDisableMinus] = useState(false);

  const buildRegister = (register: UseFormRegister<any>) => {
    return register(name as string, {
      valueAsNumber: inputNativeType === 'number',
    });
  };

  const incrementValue = () => {
    const currentCount = getValues(name);
    if (typeof currentCount === 'number') {
      setValue(name, currentCount + 1);
    }
  };

  const decrementValue = () => {
    const currentCount = getValues(name);
    if (typeof currentCount === 'number') {
      setValue(name, Math.max(minValue, currentCount - 1));
    }
  };

  const checkUpdatedValue = useWatch({
    control,
    name,
    defaultValue: 1, // default value for the watched field
  });

  useEffect(() => {
    if (checkUpdatedValue === 1) {
      setDisableMinus(true);
    } else {
      setDisableMinus(false);
    }
  }, [checkUpdatedValue]);

  return (
    <div className="pt-4">
      <label className={labelClassName}>{label}</label>
      <div className="mt-2 flex flex-row items-center justify-center">
        <button
          type="button"
          className={classNames(
            'flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary pl-7  pr-6 dark:bg-color-bg-darkmode-secondary',
            disableMinus ? 'opacity-50' : '',
          )}
          onClick={decrementValue}
          disabled={disableMinus}
        >
          <MinusBlack className="inline text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
        </button>
        <div className="mx-3">
          <input
            {...buildRegister(register)}
            {...(inputProps || {})}
            type="number"
            className="text-color-text-lightmode-placeholder::placeholder dark:text-color-text-darkmode-placeholder::placeholder h-11 w-full rounded-md border-0 bg-color-bg-lightmode-secondary pl-3 pr-3 text-center font-light text-color-text-lightmode-primary focus:outline-0 dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-primary"
          />
        </div>
        <button
          type="button"
          className="flex h-11 items-center justify-center rounded-lg bg-color-bg-lightmode-secondary pl-7 pr-6 dark:bg-color-bg-darkmode-secondary"
          onClick={incrementValue}
        >
          <PlusBlack className="inline text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
        </button>
      </div>
      <p className="bottom-1 mt-1 text-[0.75rem] text-color-text-lightmode-error dark:text-color-text-darkmode-error">
        {/* @ts-ignore */}
        {errors?.[name]?.message as any}
      </p>
    </div>
  );
};

export default Counter;
